/***************************************************************************************
 * FileName      : QuadImagePlacementManager.ts
 * Description   : 4개 꼭짓점 좌표 기반 이미지 배치 관리 서비스
 *                 - mp.quadImagePlane 컴포넌트 사용
 *                 - sdk.Scene.createNode() + node.addComponent() + node.start() 패턴
 *                 - 이미지 추가, 제거, 업데이트
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.02.11
 * Modified Date : 2026.02.11 모든 디버깅 로그 제거
 * Reference     : src/07-image-quad-placement/src/services/QuadImagePlacementManager.ts
 ***************************************************************************************/

import { initComponents } from '@mp/common';
import {
  QuadImagePlacement,
  QuadImageNodeInfo,
  DEFAULT_TEXTURE_SIZE,
  computeQuadCenter,
  cornersToLocal,
} from '../types/quad-image';

// ============================================================================
// QuadImagePlacementManager 클래스
// ============================================================================

/**
 * 2026.02.11 : 박은정 : 4개 꼭짓점 좌표 기반 이미지 배치 관리 서비스
 *
 * 접근 방식:
 *   1. 4개 꼭짓점의 중심점을 노드 위치로 설정
 *   2. 꼭짓점을 중심 기준 로컬 좌표로 변환
 *   3. mp.quadImagePlane 컴포넌트가 로컬 좌표로 BufferGeometry 생성
 *   4. 이미지를 해당 사각형에 정밀하게 렌더링
 */
export class QuadImagePlacementManager {
  /** Matterport SDK 인스턴스 */
  private sdk: any = null;

  /** 이미지 ID → 노드 정보 매핑 */
  private imageNodes: Map<string, QuadImageNodeInfo> = new Map();

  /** 초기화 완료 여부 */
  private initialized: boolean = false;

  // ==========================================================================
  // 초기화
  // ==========================================================================

  /**
   * 2026.02.11 : 박은정 : SDK 초기화 및 컴포넌트 등록
   * @param sdk Matterport SDK 인스턴스
   */
  async initialize(sdk: any): Promise<void> {
    this.sdk = sdk;

    // 2026.02.11 : 박은정 : SDK 컴포넌트 등록 (mp.quadImagePlane 포함)
    await initComponents(sdk);

    this.initialized = true;
    // 2026.02.11 : 박은정 : 디버깅 로그 제거
  }

  // ==========================================================================
  // 이미지 추가
  // ==========================================================================

  /**
   * 2026.02.11 : 박은정 : 4개 꼭짓점 좌표로 이미지 배치
   *
   * 처리 순서:
   *   1. 4개 꼭짓점의 중심점 계산
   *   2. 꼭짓점을 중심 기준 로컬 좌표로 변환
   *   3. sdk.Scene.createNode() → 노드 위치를 중심점으로 설정
   *   4. node.addComponent('mp.quadImagePlane', { 로컬 꼭짓점 좌표 })
   *   5. node.start()
   *
   * @param placement 이미지 배치 설정 (4개 꼭짓점 포함)
   * @returns 생성된 이미지 노드 정보
   */
  async addImage(placement: QuadImagePlacement): Promise<QuadImageNodeInfo> {
    if (!this.initialized || !this.sdk) {
      throw new Error('[QuadImagePlacementManager] SDK가 초기화되지 않았습니다.');
    }

    // 2026.02.11 : 박은정 : 중복 ID 체크
    if (this.imageNodes.has(placement.id)) {
      // 2026.02.11 : 박은정 : 디버깅 로그 제거
      await this.removeImage(placement.id);
    }
    // 2026.02.11 : 박은정 : 디버깅 로그 제거

    // 2026.02.11 : 박은정 : 중심점 계산
    const center = computeQuadCenter(placement.corners);

    // 2026.02.11 : 박은정 : 로컬 좌표 변환
    const localCorners = cornersToLocal(placement.corners, center);

    // 2026.02.11 : 박은정 : 텍스처 크기
    const textureSize = placement.textureSize || DEFAULT_TEXTURE_SIZE;

    // 2026.02.11 : 박은정 : Scene Node 생성
    const node = await this.sdk.Scene.createNode();

    // 2026.02.11 : 박은정 : 노드 위치를 중심점으로 설정
    node.position.set(center.x, center.y, center.z);

    // 2026.02.11 : 박은정 : mp.quadImagePlane 컴포넌트 추가 (로컬 좌표 전달)
    const component = node.addComponent('mp.quadImagePlane', {
      src: placement.imageUrl,
      textureWidth: textureSize.w,
      textureHeight: textureSize.h,
      topLeft: localCorners.topLeft,
      topRight: localCorners.topRight,
      bottomLeft: localCorners.bottomLeft,
      bottomRight: localCorners.bottomRight,
      transparent: true,
      opacity: placement.opacity,
      visible: placement.visible,
      doubleSide: true,
    });

    // 2026.02.11 : 박은정 : 디버깅 로그 제거
    // 2026.02.11 : 박은정 : node.start() → onInit() 호출
    node.start();
    // 2026.02.11 : 박은정 : 디버깅 로그 제거

    // 2026.02.11 : 박은정 : 노드 정보 저장
    const nodeInfo: QuadImageNodeInfo = {
      id: placement.id,
      placement: { ...placement },
      node,
      component,
    };

    this.imageNodes.set(placement.id, nodeInfo);
    // 2026.02.11 : 박은정 : 디버깅 로그 제거

    return nodeInfo;
  }

  // ==========================================================================
  // 이미지 제거
  // ==========================================================================

  /**
   * 2026.02.11 : 박은정 : 특정 이미지 제거
   * @param id 이미지 ID
   */
  async removeImage(id: string): Promise<void> {
    const nodeInfo = this.imageNodes.get(id);
    if (nodeInfo) {
      nodeInfo.node.stop();
    }
    this.imageNodes.delete(id);
    // 2026.02.11 : 박은정 : 디버깅 로그 제거
  }

  /**
   * 2026.02.11 : 박은정 : 모든 이미지 제거
   */
  async removeAll(): Promise<void> {
    const ids = Array.from(this.imageNodes.keys());
    for (const id of ids) {
      await this.removeImage(id);
    }
    // 2026.02.11 : 박은정 : 디버깅 로그 제거
  }

  // ==========================================================================
  // 이미지 업데이트
  // ==========================================================================

  /**
   * 2026.02.11 : 박은정 : 이미지 속성 업데이트
   * @param id 이미지 ID
   * @param updates 업데이트할 속성
   */
  updateImage(id: string, updates: Partial<QuadImagePlacement>): void {
    const nodeInfo = this.imageNodes.get(id);
    if (!nodeInfo) {
      // 2026.02.11 : 박은정 : 디버깅 로그 제거
      return;
    }

    const { component, placement } = nodeInfo;

    // 2026.02.11 : 박은정 : 투명도 업데이트
    if (updates.opacity !== undefined) {
      component.inputs.opacity = updates.opacity;
      placement.opacity = updates.opacity;
    }

    // 2026.02.11 : 박은정 : 표시 여부 업데이트
    if (updates.visible !== undefined) {
      component.inputs.visible = updates.visible;
      placement.visible = updates.visible;
    }

    // 2026.02.11 : 박은정 : 이미지 URL 업데이트
    if (updates.imageUrl) {
      component.inputs.src = updates.imageUrl;
      placement.imageUrl = updates.imageUrl;
    }

    // 2026.02.11 : 박은정 : 꼭짓점 좌표 업데이트 (전체 재배치)
    if (updates.corners) {
      const center = computeQuadCenter(updates.corners);
      const localCorners = cornersToLocal(updates.corners, center);

      nodeInfo.node.position.set(center.x, center.y, center.z);
      component.inputs.topLeft = localCorners.topLeft;
      component.inputs.topRight = localCorners.topRight;
      component.inputs.bottomLeft = localCorners.bottomLeft;
      component.inputs.bottomRight = localCorners.bottomRight;

      placement.corners = { ...updates.corners };
    }
  }

  // ==========================================================================
  // 조회
  // ==========================================================================

  /**
   * 2026.02.11 : 박은정 : 배치된 이미지 수
   */
  getImageCount(): number {
    return this.imageNodes.size;
  }

  /**
   * 2026.02.11 : 박은정 : 초기화 상태 확인
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * 2026.02.11 : 박은정 : 특정 이미지 노드 정보 조회
   */
  getImageNode(id: string): QuadImageNodeInfo | undefined {
    return this.imageNodes.get(id);
  }

  /**
   * 2026.02.11 : 박은정 : 모든 이미지 노드 정보 조회
   */
  getAllImageNodes(): QuadImageNodeInfo[] {
    return Array.from(this.imageNodes.values());
  }
}

// ============================================================================
// 싱글톤 인스턴스
// ============================================================================

// 2026.02.11 : 박은정 : 전역 싱글톤 인스턴스
let managerInstance: QuadImagePlacementManager | null = null;

/**
 * 2026.02.11 : 박은정 : QuadImagePlacementManager 싱글톤 인스턴스 반환
 */
export function getQuadImagePlacementManager(): QuadImagePlacementManager {
  if (!managerInstance) {
    managerInstance = new QuadImagePlacementManager();
  }
  return managerInstance;
}

